export default defineNuxtPlugin(() => {
  if (!import.meta.client) {
    return;
  }

  // Desktop / cashier only. Phones freeze & discard background tabs, so the
  // BroadcastChannel handshake can't work reliably there (a frozen incumbent
  // can't answer, and a discarded original tab that reloads on return could
  // even redirect ITSELF). Staff phones also open many tabs on purpose.
  //
  // A cashier terminal is a desktop OR a large touchscreen/tablet, so we can't
  // key off "touch = phone". Instead we treat "has a mouse" OR "wide screen" as
  // desktop/cashier; a phone (coarse pointer + narrow viewport) is skipped.
  // Tune the 900px threshold if some cashier layout runs narrower.
  const isDesktopClass =
    window.matchMedia('(pointer: fine)').matches ||
    window.matchMedia('(min-width: 900px)').matches;
  if (!isDesktopClass) {
    return;
  }

  // Single-tab guard, scoped to the current sub-domain.
  //
  //   - As soon as a tab loads it announces itself with HELLO.
  //   - Any already-established tab on the SAME sub-domain answers OCCUPIED.
  //   - The newcomer, on hearing OCCUPIED, redirects ITSELF to the static
  //     "single tab only" page — the existing tab keeps working untouched.
  //
  // BroadcastChannel is already origin-scoped by the browser, and we further
  // key the channel name by hostname so each sub-domain (e.g. app2.niceloop.com
  // vs a hosting-preview URL) behaves independently and can be tested in
  // isolation.
  const MSG_HELLO = 'niceloop-pos-hello';
  const MSG_OCCUPIED = 'niceloop-pos-occupied';
  const SINGLE_TAB_PAGE = '/pos-multi-tabs-opened.html';
  const CHANNEL_NAME = `niceloop-pos-tab:${window.location.hostname}`;

  let established = false;
  let redirected = false;

  const channel = new BroadcastChannel(CHANNEL_NAME);

  channel.addEventListener('message', (msg) => {
    if (msg.data === MSG_HELLO) {
      // A new tab is trying to open. If we already own this sub-domain, object.
      if (established) {
        channel.postMessage(MSG_OCCUPIED);
      }
    } else if (msg.data === MSG_OCCUPIED) {
      // Another active tab already owns this sub-domain — we are the newcomer.
      if (redirected) {
        return;
      }
      redirected = true;
      channel.close();
      window.location.replace(SINGLE_TAB_PAGE);
    }
  });

  // Announce ourselves; incumbents (if any) reply OCCUPIED right away.
  channel.postMessage(MSG_HELLO);

  // If nobody objected within the grace window, we are the sole tab and
  // become the one that answers future newcomers.
  const graceTimer = setTimeout(() => {
    established = true;
  }, 500);

  window.addEventListener('beforeunload', () => {
    clearTimeout(graceTimer);
    channel.close();
  });
});
